import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useWhatsAppConfig, waLink, WhatsAppIcon } from "@/components/WhatsAppButton";
import { serviceIcon } from "@/components/serviceIcons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, ChevronRight, ArrowLeft, Loader2 } from "lucide-react";

const MAX_MSG = 1500;
const MAX_ITEMS = 10;
const pad = (n) => String(n).padStart(2, "0");
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const clean = (v) => String(v || "").replace(/[\u0000-\u001f\u007f<>*_~`]/g, " ").replace(/\s+/g, " ").trim();

function fmtValue(type, v) {
  if (type === "date") return new Date(`${v}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  if (type === "month") return new Date(`${v}-01T00:00:00`).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  return v;
}

export function buildMessage(items, member) {
  const line = (it, i, note) => {
    const parts = it.order.filter((k) => it.values[k]).map((k) => `${it.labels[k]}: ${fmtValue(it.types[k], it.values[k])}`);
    if (note) parts.push(`Note: ${note}`);
    return `${i + 1}) ${it.service_name}: ${parts.join(", ")}`;
  };
  const footer = `Member: ${clean(member.name)} | ID: ${clean(member.id)} | Mobile: ${clean(member.mobile)}`;
  const notes = items.map((it) => it.note || "");
  const compose = () => ["Hi Zola, I'd like to book:", ...items.map((it, i) => line(it, i, notes[i])), footer].join("\n");
  let msg = compose();
  while (msg.length > MAX_MSG && notes.some((n) => n)) {
    const idx = notes.reduce((best, n, i) => (n.length > notes[best].length ? i : best), 0);
    const cut = Math.max(0, notes[idx].length - (msg.length - MAX_MSG) - 1);
    notes[idx] = cut > 3 ? `${notes[idx].slice(0, cut - 1)}…` : "";
    msg = compose();
  }
  return msg.length > MAX_MSG ? `${msg.slice(0, MAX_MSG - 1)}…` : msg;
}

function FieldInput({ f, value, onChange }) {
  const common = { id: `f-${f.key}`, value: value || "", onChange: (e) => onChange(e.target.value),
                   "data-testid": `svc-field-${f.key}`, required: f.required };
  if (f.type === "date") return <Input type="date" min={todayStr()} {...common} />;
  if (f.type === "month") return <Input type="month" min={todayStr().slice(0, 7)} {...common} />;
  if (f.type === "number") return <Input type="number" inputMode="numeric" min={1} max={99} {...common} />;
  return <Input maxLength={80} {...common} />;
}

function ServiceFormDialog({ service, onClose, onAdded }) {
  const [values, setValues] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setValues({}); setNote(""); }, [service]);
  if (!service) return null;
  const Icon = serviceIcon(service.icon);

  const add = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.post("/booking-enquiries/validate", { service_id: service.id, values, note: note || null });
      onAdded({ ...data, uid: `${service.id}-${Date.now()}`,
                types: Object.fromEntries(service.fields.map((f) => [f.key, f.type])) }, next);
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="service-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Icon className="h-5 w-5 gold" /> {service.name}</DialogTitle>
        </DialogHeader>
        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); add("summary"); }}>
          {service.fields.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label htmlFor={`f-${f.key}`}>{f.label}{f.required ? "" : " (optional)"}</Label>
              <FieldInput f={f} value={values[f.key]} onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))} />
            </div>
          ))}
          <div className="space-y-1">
            <Label htmlFor="svc-note">Note (optional)</Label>
            <Textarea id="svc-note" rows={2} maxLength={200} value={note} onChange={(e) => setNote(e.target.value)}
                      data-testid="svc-field-note" />
            <div className="text-[11px] text-right" style={{ color: "var(--text-muted)" }}>{note.length}/200</div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2 flex-col-reverse sm:flex-row">
            <Button type="button" variant="outline" disabled={busy} onClick={() => add("tiles")} data-testid="svc-add-another">
              <Plus className="h-4 w-4 mr-1" /> Add another service
            </Button>
            <Button type="submit" disabled={busy} className="bg-navy text-white" data-testid="svc-add-to-enquiry">
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Add to enquiry
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Tiles({ services, onPick }) {
  if (services.length === 0)
    return <div className="w-card-flat p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}
                data-testid="services-empty">No services available.</div>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="service-tiles">
      {services.map((s) => {
        const Icon = serviceIcon(s.icon);
        return (
          <button key={s.id} type="button" onClick={() => onPick(s)} data-testid={`service-tile-${s.id}`}
                  className="w-card w-card-bordered lift p-4 min-h-[110px] flex flex-col items-start justify-between text-left">
            <span className="h-10 w-10 rounded-full grid place-items-center" style={{ background: "var(--bg-soft)" }}>
              <Icon className="h-5 w-5 gold" />
            </span>
            <span className="font-semibold text-sm mt-3">{s.name}</span>
          </button>
        );
      })}
    </div>
  );
}

function Summary({ items, member, onRemove, onAddMore, waNumber }) {
  const message = useMemo(() => buildMessage(items, member), [items, member]);
  const send = () => {
    if (!waNumber || items.length === 0) return;
    window.open(waLink(waNumber, message), "_blank", "noopener,noreferrer");
    api.post("/booking-enquiries/log", { service_ids: items.map((i) => i.service_id) }).catch(() => {});
  };
  return (
    <div className="space-y-4" data-testid="enquiry-summary">
      <ol className="space-y-2">
        {items.map((it, i) => (
          <li key={it.uid} className="w-card w-card-bordered p-4 flex items-start justify-between gap-3" data-testid={`enquiry-item-${i}`}>
            <div className="min-w-0">
              <div className="font-semibold">{i + 1}. {it.service_name}</div>
              <div className="text-xs mt-1 break-words" style={{ color: "var(--text-muted)" }}>
                {it.order.filter((k) => it.values[k]).map((k) => `${it.labels[k]}: ${fmtValue(it.types[k], it.values[k])}`).join(" · ")}
                {it.note ? ` · Note: ${it.note}` : ""}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => onRemove(it.uid)} aria-label="Remove service"
                    data-testid={`enquiry-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
          </li>
        ))}
      </ol>
      <div className="w-card-flat p-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm" data-testid="enquiry-member">
        {[["Name", member.name], ["Member ID", member.id], ["Mobile", member.mobile]].map(([k, v]) => (
          <div key={k}><div className="label-caps">{k}</div><div className="font-medium mt-0.5">{v || "—"}</div></div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        {items.length < MAX_ITEMS && (
          <Button variant="outline" className="rounded-full h-11" onClick={onAddMore} data-testid="summary-add-another">
            <Plus className="h-4 w-4 mr-1" /> Add another service
          </Button>
        )}
        <Button onClick={send} disabled={!waNumber || items.length === 0} data-testid="send-on-whatsapp"
                className="rounded-full h-11 text-white flex-1 sm:flex-none" style={{ background: waNumber ? "#25D366" : undefined }}>
          <WhatsAppIcon className="h-5 w-5 mr-2" /> Send on WhatsApp
        </Button>
      </div>
      {!waNumber && <p className="text-sm text-red-700" data-testid="wa-unavailable">WhatsApp enquiries unavailable</p>}
      <p className="text-xs" style={{ color: "var(--text-muted)" }}>
        Opens WhatsApp with your enquiry ready — nothing is sent until you press send there. No payment or booking is made here.
      </p>
    </div>
  );
}

export default function BookTrip() {
  const { user } = useAuth();
  const wa = useWhatsAppConfig();
  const waNumber = wa?.enabled ? wa.number : null;
  const [services, setServices] = useState(null);
  const [picked, setPicked] = useState(null);
  const [items, setItems] = useState([]);
  const [view, setView] = useState("tiles");
  const p = user?.profile || {};
  const member = { name: p.name || user?.name, id: p.customer_id, mobile: p.phone };

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch((e) => { setServices([]); toast.error(formatError(e)); });
  }, []);

  const onAdded = (item, next) => {
    setItems((s) => [...s, item].slice(0, MAX_ITEMS));
    setPicked(null);
    setView(next);
    toast.success(`${item.service_name} added to your enquiry`);
  };

  return (
    <AppShell title="Book a Trip" subtitle="Pick one or more services and send a single enquiry to Zola on WhatsApp.">
      {view === "summary" && items.length > 0 ? (
        <>
          <button type="button" onClick={() => setView("tiles")} className="mb-4 inline-flex items-center gap-1 text-sm"
                  style={{ color: "var(--text-muted)" }} data-testid="back-to-services">
            <ArrowLeft className="h-4 w-4" /> Services
          </button>
          <Summary items={items} member={member} waNumber={waNumber}
                   onRemove={(uid) => setItems((s) => s.filter((i) => i.uid !== uid))} onAddMore={() => setView("tiles")} />
        </>
      ) : services === null ? (
        <div className="text-sm" style={{ color: "var(--text-muted)" }}>Loading services…</div>
      ) : (
        <>
          <Tiles services={services} onPick={setPicked} />
          {items.length > 0 && (
            <button type="button" onClick={() => setView("summary")} data-testid="review-enquiry"
                    className="mt-6 w-full sm:w-auto inline-flex items-center justify-between gap-4 rounded-full h-12 px-5 bg-navy text-white font-semibold">
              {items.length} service{items.length > 1 ? "s" : ""} in your enquiry · Review <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </>
      )}
      <ServiceFormDialog service={picked} onClose={() => setPicked(null)} onAdded={onAdded} />
    </AppShell>
  );
}
